/**
 * Clients: every stack of a view, side by side. Shown for a view, never for
 * one stack.
 *
 * Three ways to read the same stacks:
 *
 *   - Table: one row per stack. A column switch swaps the numbers shown, so
 *     the table never needs sideways scrolling. Stacks that share one AWS
 *     account are one row, because AWS bills the account, not the stack.
 *   - Compare: two to four stacks, metric by metric, with one daily chart.
 *   - Health map: every stack as a dot. Closer to the centre means more of
 *     its people are active.
 *
 * This is the only per-stack table on the dashboard. Other tabs link here.
 * Every number is read from a selector.
 */
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Form, Nav, Table } from 'react-bootstrap';
import { XLg } from 'react-bootstrap-icons';
import type { ChartOptions } from 'chart.js';
import { Line } from 'react-chartjs-2';
import '../chartSetup';
import { ND_CHART_PAL, ND_COLORS } from '@/styles/theme';
import type { ClientSnapshot } from '@/types/fleetAnalytics';
import { FileExportService } from '@/utils/fileExport';
import { Constellation } from '../Constellation';
import { useCurrency } from '../currencyContext';
import type { FleetData, FleetTier } from '../fleetData';
import { buildFleetPlanets, computeFleetMap, fleetRingLabels } from '../fleetMap';
import type { FleetMapMetrics, FleetMode } from '../fleetMap';
import { RowDownloadMenu } from '../RowDownloadMenu';
import type { RowDownloadOption } from '../RowDownloadMenu';
import { isReady, needsOf, selectSeries, selectStackRows } from '../selectors';
import type { SeriesKey, StackRow } from '../selectors';
import { FLEET_VIEWS, fmtN, fmtUSD, fmtUSD0, isInfraAccount, snapshotForDisplay } from '../shared';
import type { WindowState } from '../shared';
import { Empty, NeedsTier, Panel, ScopeTag } from '../ui/kit';
import type { Scope } from '../ui/kit';
import {
  Bar,
  dayLabel,
  fmtCredits,
  fmtMinutes,
  FreshnessMark,
  LinkButton,
  moneyScale,
  plural,
  Quiet,
  SearchBox,
  SortTh,
  Swatch,
  useSort,
} from './common';
import type { DashboardTabProps, Selection } from './registry';
import './Clients.css';

// ─── formats ─────────────────────────────────────────────────────────────

const fmtRate = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 1 });
const signedPct = (ratio: number) => `${ratio >= 0 ? '+' : ''}${(ratio * 100).toFixed(0)}%`;

const ORG_LABEL: Record<string, string> = { nextgen: 'NextGen', standalone: 'Standalone', arcanum: 'Arcanum' };
const SCOPE_WORDS: Record<Scope, string> = {
  window: '',
  now: 'now',
  month: 'this month',
  'last-30d': 'last 30 days',
  'all-time': 'all time',
};

// ─── the columns ─────────────────────────────────────────────────────────
//
// One list of columns feeds the table, the compare view and the download, so
// a number has one name and one meaning in all three.

type SetKey = 'spend' | 'usage' | 'credits' | 'value' | 'apps';

interface Column {
  key: string;
  /** The table header. Short. */
  label: string;
  /** The name where there is room: the compare view and the download. */
  long?: string;
  /** The period the column covers. `window` follows the time control and carries no tag. */
  scope: Scope;
  /** What the number is, shown when a person hovers the header. */
  hint: string;
  value: (row: StackRow) => number | null;
  format: (n: number) => string;
  /** The data an empty cell is still waiting for. */
  waitsFor?: FleetTier[];
  /** What an empty cell says once everything is loaded, and why. */
  empty: { text: string; why: string };
  /** In US dollars. The download says so in the column's name. */
  usd?: boolean;
  /** Draw a bar beside the number in the compare view. Off for a number that can go below zero. */
  bar?: boolean;
  /** True for a row whose number is close but not exact. */
  approx?: (row: StackRow) => boolean;
  /** The value is a word (on, off), held as 1 or 0 so the column still sorts. The download writes the word. */
  asText?: boolean;
  /**
   * A column of words that are not a scale (a contract). The cell, the sort
   * and the download read this, and `value` is not read.
   */
  text?: (row: StackRow) => string | null;
  /** The hover on a cell of a `text` column. */
  textHint?: (row: StackRow) => string | undefined;
}

const NOT_KNOWN = { text: 'n/a', why: 'Not known for this stack.' };
const NOT_METERED = { text: 'not metered', why: 'This stack does not meter credits.' };

function columnSets(timeSaved: Scope): Record<SetKey, { label: string; columns: Column[] }> {
  return {
    spend: {
      label: 'Spend',
      columns: [
        {
          key: 'spend',
          label: 'Total spend',
          scope: 'window',
          hint: 'What the stack cost to run: the AWS bill with model use moved to the stack that used it.',
          value: (r) => r.spend,
          format: fmtUSD,
          waitsFor: ['services'],
          empty: NOT_KNOWN,
          usd: true,
          bar: true,
        },
        {
          key: 'awsBill',
          label: 'AWS bill',
          scope: 'window',
          hint: "What AWS billed the stack's account. For a standalone customer: Numa's part of the bill.",
          value: (r) => r.awsBill,
          format: fmtUSD,
          empty: NOT_KNOWN,
          usd: true,
          bar: true,
        },
        {
          key: 'chatCost',
          label: 'Model spend',
          long: 'Model spend from chat and agents',
          scope: 'window',
          hint: 'Model use in chat and agent runs, priced from token counts.',
          value: (r) => r.chatCost,
          format: fmtUSD,
          empty: NOT_KNOWN,
          usd: true,
          bar: true,
        },
        {
          key: 'costPerConversation',
          label: 'Per conversation',
          long: 'Model spend per conversation',
          scope: 'window',
          hint: 'Model spend divided by the conversations that started in the window.',
          value: (r) => r.costPerConversation,
          format: fmtUSD,
          empty: { text: 'n/a', why: 'No conversation started in the window.' },
          usd: true,
          bar: true,
          approx: (r) => r.conversationsApprox,
        },
        {
          key: 'costPerActivePerson',
          label: 'Per active person',
          long: 'Model spend per active person',
          scope: 'window',
          hint: 'Model spend from chat and agents divided by the people who sent a message in the window. The same figure as on the Cost and People tabs.',
          value: (r) => r.costPerActivePerson,
          format: fmtUSD,
          waitsFor: ['users'],
          empty: { text: 'n/a', why: 'Nobody sent a message in the window.' },
          usd: true,
          bar: true,
        },
        {
          key: 'spendPerActivePerson',
          label: 'Total per active person',
          long: 'Total spend per active person',
          scope: 'window',
          hint: 'Total spend, the AWS bill included, divided by the people who sent a message in the window.',
          value: (r) => r.spendPerActivePerson,
          format: fmtUSD,
          waitsFor: ['services', 'users'],
          empty: { text: 'n/a', why: 'Nobody sent a message in the window.' },
          usd: true,
          bar: true,
        },
      ],
    },
    usage: {
      label: 'Usage',
      columns: [
        {
          key: 'conversations',
          label: 'Conversations',
          scope: 'window',
          hint: 'Conversations that started in the window.',
          value: (r) => r.conversations,
          format: fmtN,
          empty: NOT_KNOWN,
          bar: true,
          approx: (r) => r.conversationsApprox,
        },
        {
          key: 'messages',
          label: 'Messages',
          scope: 'window',
          hint: 'Messages people sent.',
          value: (r) => r.messages,
          format: fmtN,
          empty: NOT_KNOWN,
          bar: true,
        },
        {
          key: 'activeUsers',
          label: 'Active people',
          scope: 'window',
          hint: 'People who sent a message in the window, each counted once.',
          value: (r) => r.activeUsers,
          format: fmtN,
          waitsFor: ['users'],
          empty: NOT_KNOWN,
          bar: true,
        },
        {
          key: 'provisionedUsers',
          label: 'Provisioned',
          long: 'Provisioned people',
          scope: 'now',
          hint: 'People with a login.',
          value: (r) => r.provisionedUsers,
          format: fmtN,
          empty: NOT_KNOWN,
          bar: true,
        },
        {
          key: 'messagesPerActivePerson',
          label: 'Messages per person',
          long: 'Messages per active person',
          scope: 'window',
          hint: 'Messages divided by the people who sent one in the window.',
          value: (r) => r.messagesPerActivePerson,
          format: fmtRate,
          waitsFor: ['users'],
          empty: { text: 'n/a', why: 'Nobody sent a message in the window.' },
          bar: true,
        },
      ],
    },
    credits: {
      label: 'Credits',
      columns: [
        {
          key: 'contract',
          label: 'Contract',
          scope: 'now',
          hint: "The client's contract, from the portal's record. A shared AWS account lists each contract on it.",
          value: () => null,
          text: (r) => r.contract,
          textHint: (r) => r.contractHint,
          format: String,
          empty: { text: 'none', why: 'Nobody has set a contract for this client.' },
        },
        {
          key: 'collectible',
          label: 'Collectible',
          long: 'Billed for credits',
          scope: 'now',
          hint: 'Yes when Arcanum invoices the client for credits used past its allocation: the Credits contract, or a partner billed as Credits, and not Inactive. Only these clients owe credits.',
          value: (r) => (r.collectible === 'yes' ? 1 : r.collectible === 'some' ? 0.5 : 0),
          format: (n) => (n === 1 ? 'Yes' : n === 0 ? 'No' : 'Some'),
          empty: NOT_KNOWN,
          asText: true,
        },
        {
          key: 'creditsWindow',
          label: 'Credits used',
          long: 'Credits used in the window',
          scope: 'window',
          hint: 'Credits charged in the window. Credits by day go back four billing months.',
          value: (r) => r.creditsWindow,
          format: fmtCredits,
          empty: NOT_METERED,
          bar: true,
        },
        {
          key: 'creditsMonth',
          label: 'Used this month',
          long: 'Credits used this month',
          scope: 'month',
          hint: 'Credits charged in the current New Zealand billing month.',
          value: (r) => r.creditsMonth,
          format: fmtCredits,
          empty: {
            text: 'n/a',
            why: 'This stack does not meter credits, or its last update is from an earlier billing month.',
          },
          bar: true,
        },
        {
          key: 'creditsBalance',
          label: 'Balance',
          long: 'Credit balance',
          scope: 'now',
          hint: 'Top-ups and settlements, less use over the monthly allocation that is not settled yet. Below zero, a client billed for credits owes them. Any other client owes nothing.',
          value: (r) => r.creditsBalance,
          format: fmtCredits,
          empty: NOT_METERED,
        },
      ],
    },
    value: {
      label: 'Value',
      columns: [
        {
          key: 'timeSavedMinutes',
          label: 'Time saved',
          scope: timeSaved,
          hint: 'Each scheduled run of an agent saves the minutes its owner estimated for that agent.',
          value: (r) => r.timeSavedMinutes,
          format: fmtMinutes,
          empty: NOT_KNOWN,
          bar: true,
        },
        {
          key: 'scheduledRuns',
          label: 'Scheduled runs',
          scope: 'window',
          hint: 'Runs an automation made in the window.',
          value: (r) => r.scheduledRuns,
          format: fmtN,
          empty: NOT_KNOWN,
          bar: true,
        },
        {
          key: 'activeSchedules',
          label: 'Active schedules',
          scope: 'now',
          hint: 'Schedules that still run.',
          value: (r) => r.activeSchedules,
          format: fmtN,
          empty: NOT_KNOWN,
          bar: true,
        },
        {
          key: 'agents',
          label: 'Agents',
          scope: 'now',
          hint: 'Workspace agents and personal agents.',
          value: (r) => r.agents,
          format: fmtN,
          empty: NOT_KNOWN,
          bar: true,
        },
        {
          key: 'knowledgeBases',
          label: 'Knowledge bases',
          scope: 'now',
          hint: 'Knowledge bases set up on the stack.',
          value: (r) => r.knowledgeBases,
          format: fmtN,
          empty: NOT_KNOWN,
          bar: true,
        },
        {
          key: 'integrations',
          label: 'Integrations',
          scope: 'now',
          hint: 'Different integrations that one person or more has connected.',
          value: (r) => r.integrations,
          format: fmtN,
          empty: NOT_KNOWN,
          bar: true,
        },
      ],
    },
    apps: {
      label: 'Apps',
      columns: [
        {
          key: 'apps',
          label: 'Published',
          long: 'Apps and dashboards published',
          scope: 'now',
          hint: 'Apps and dashboards with a published version.',
          value: (r) => r.apps,
          format: fmtN,
          empty: { text: 'not available', why: "This stack's data holds no record of apps yet." },
          bar: true,
        },
        {
          key: 'appRuns',
          label: 'Runs',
          long: 'App runs',
          scope: 'window',
          hint: 'Runs of a published app or dashboard, by a person or an automation. Test runs are not counted.',
          value: (r) => r.appRuns,
          format: fmtN,
          empty: { text: 'not available', why: "This stack's data holds no run history for apps yet." },
          bar: true,
        },
        {
          key: 'appBuilder',
          label: 'App Builder',
          scope: 'now',
          hint: 'Can people on this stack build apps? Without the App Builder they can still publish dashboards from chat.',
          value: (r) => (r.appBuilder === null ? null : r.appBuilder ? 1 : 0),
          format: (n) => (n ? 'on' : 'off'),
          empty: { text: 'not available', why: "This stack's data does not say if the App Builder is switched on." },
          asText: true,
        },
      ],
    },
  };
}

const SET_ORDER: SetKey[] = ['spend', 'usage', 'credits', 'value', 'apps'];

// ─── small shared pieces ─────────────────────────────────────────────────

/** One cell of a stack's numbers: the number, a quiet "still loading", or what an empty cell means. */
function cellContent(col: Column, row: StackRow, pending: FleetTier[]): ReactNode {
  if (col.text) {
    const words = col.text(row);
    if (!words) return <Quiet title={col.empty.why}>{col.empty.text}</Quiet>;
    return <span title={col.textHint?.(row)}>{words}</span>;
  }
  const v = col.value(row);
  if (v === null) {
    if (col.waitsFor?.some((t) => pending.includes(t))) return <Quiet title="Loading">…</Quiet>;
    return <Quiet title={col.empty.why}>{col.empty.text}</Quiet>;
  }
  // Below zero is owed only by a client billed for credits. Any other client's use past its allocation is not billed.
  if (col.key === 'creditsBalance' && v < 0) {
    if (row.collectible === 'yes') return <span className="clients-owed">{col.format(-v)} owed</span>;
    return <span title={NOT_BILLED_NOTE}>{col.format(v)}</span>;
  }
  const text = col.format(v);
  return col.approx?.(row) ? <span title={APPROX_NOTE}>~{text}</span> : text;
}

const APPROX_NOTE =
  "This stack's data counts a conversation once for each day it was active, so the count reads a little high.";

const NOT_BILLED_NOTE =
  'Below zero, but this client is not billed for credits, so it owes nothing. Its use past the allocation is not billed.';

/** How many of a shared account's stacks are named under its row before "and N more". */
const MEMBERS_NAMED = 6;

/**
 * The words under a row's name: its organisation and its region. A row that
 * stands for a shared AWS account names the stacks on it, each a link to
 * that stack.
 */
function rowMeta(row: StackRow, onSelectClient: (id: string | null) => void): ReactNode {
  const parts = [row.org ? ORG_LABEL[row.org] : null, row.region].filter(Boolean).join(' · ');
  if (row.members.length < 2) return parts;
  const more = row.members.length - MEMBERS_NAMED;
  return (
    <>
      {parts}
      {parts && ' · '}
      One AWS account with {plural(row.members.length, 'stack')}:{' '}
      {row.members.slice(0, MEMBERS_NAMED).map((member, i) => (
        <span key={member}>
          {i > 0 && ', '}
          <LinkButton onClick={() => onSelectClient(member)} title={`Open ${member}`}>
            {member}
          </LinkButton>
        </span>
      ))}
      {more > 0 && ` and ${fmtN(more)} more`}
    </>
  );
}

// ─── the table ───────────────────────────────────────────────────────────

/** The table sorts by a column's key, or by `name`. Names and words start A to Z, numbers start largest first. */
const NAME_COLUMN: ReadonlySet<string> = new Set(['name', 'contract']);

interface StacksProps {
  /** One row for each stack. Stacks that share an AWS account are one row. */
  rows: StackRow[];
  timeSaved: Scope;
  pending: FleetTier[];
  /** Accounts with no stack in the view, which this table leaves out. */
  infraCount: number;
  /** Inactive stacks the view hides. Their spend is in the view and in no row. */
  hiddenCount: number;
  selection: Selection;
  win: WindowState;
  onSelectClient: (id: string | null) => void;
}

function StacksTable({
  rows,
  timeSaved,
  pending,
  infraCount,
  hiddenCount,
  selection,
  win,
  onSelectClient,
}: StacksProps) {
  useCurrency();
  const [setKey, setSetKey] = useState<SetKey>('spend');
  const { sort, onSort, setSort } = useSort<string>({ key: 'spend', dir: 'desc' }, NAME_COLUMN);
  const [query, setQuery] = useState('');
  const [oldOnly, setOldOnly] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const sets = useMemo(() => columnSets(timeSaved), [timeSaved]);
  const columns = sets[setKey].columns;
  const allColumns = useMemo(() => SET_ORDER.flatMap((k) => sets[k].columns), [sets]);

  const oldCount = useMemo(() => rows.filter((r) => r.stale || r.failed).length, [rows]);
  const stackCount = useMemo(() => rows.reduce((n, r) => n + r.members.length, 0), [rows]);
  const sharedCount = useMemo(() => rows.filter((r) => r.members.length > 1).length, [rows]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const kept = rows.filter((r) => {
      if (oldOnly && !(r.stale || r.failed)) return false;
      if (!q) return true;
      return (
        r.label.toLowerCase().includes(q) ||
        (r.accountId || '').includes(q) ||
        r.members.some((member) => member.toLowerCase().includes(q))
      );
    });
    const col = allColumns.find((c) => c.key === sort.key);
    const byName = (a: StackRow, b: StackRow) => a.label.localeCompare(b.label);
    return kept.sort((a, b) => {
      if (!col) return sort.dir === 'asc' ? byName(a, b) : byName(b, a);
      if (col.text) {
        const at = col.text(a);
        const bt = col.text(b);
        // A row with no words goes last whichever way the column is sorted.
        if (!at || !bt) return at === bt ? byName(a, b) : !at ? 1 : -1;
        return (sort.dir === 'asc' ? at.localeCompare(bt) : bt.localeCompare(at)) || byName(a, b);
      }
      const av = col.value(a);
      const bv = col.value(b);
      // A stack with no number goes last whichever way the column is sorted.
      if (av === null || bv === null) return av === bv ? byName(a, b) : av === null ? 1 : -1;
      return (sort.dir === 'desc' ? bv - av : av - bv) || byName(a, b);
    });
  }, [rows, query, oldOnly, sort, allColumns]);

  const approxShown = useMemo(
    () => columns.some((c) => c.approx) && shown.some((r) => r.conversationsApprox),
    [columns, shown]
  );

  const pickSet = (next: SetKey) => {
    setSetKey(next);
    // Sorting by a column that is no longer on screen would look like no order at all.
    // The new set sorts by its first number, largest first.
    const first = sets[next].columns.find((c) => !c.text && !c.asText) ?? sets[next].columns[0];
    setSort((prev) => (prev.key === 'name' ? prev : { key: first.key, dir: 'desc' }));
  };

  const downloads: RowDownloadOption[] = useMemo(() => {
    const stamp = `${selection.id.replace(/[^a-zA-Z0-9_-]/g, '')}-${win.startDate}_${win.endDate}`;
    const header = (c: Column) =>
      `${c.long ?? c.label}${c.usd ? ' (USD)' : ''}${c.scope === 'window' ? '' : ` [${SCOPE_WORDS[c.scope]}]`}`;
    const save = (name: string, content: string, mimeType: string) =>
      FileExportService.downloadFile({ name, content, mimeType, size: new Blob([content]).size });
    return [
      {
        key: 'csv',
        label: 'Table (.csv)',
        sublabel: 'The rows listed, with every column set. Money in US dollars.',
        run: async () => {
          const records = shown.map((r) => {
            const rec: Record<string, string | number> = {
              Stack: r.label,
              Kind: r.kind,
              Organisation: r.org ?? '',
              Region: r.region ?? '',
              'AWS account': r.accountId ?? '',
              'Stacks on the account': r.members.join('; '),
            };
            for (const c of allColumns) {
              if (c.text) {
                rec[header(c)] = c.text(r) ?? '';
                continue;
              }
              const v = c.value(r);
              rec[header(c)] = v === null ? '' : c.asText ? c.format(v) : v;
            }
            rec['Last updated (UTC)'] = r.generatedAt ?? '';
            rec['Old data'] = r.stale ? 'yes' : 'no';
            rec['Last update failed'] = r.failed ? 'yes' : 'no';
            return rec;
          });
          if (!records.length) throw new Error('No stacks are listed, so there is nothing to download.');
          save(`numa-clients-${stamp}.csv`, FileExportService.arrayToCSV(records, Object.keys(records[0])), 'text/csv');
        },
      },
      {
        key: 'json',
        label: 'Rows (.json)',
        sublabel: 'The same stacks as data, for a script.',
        run: async () => {
          const payload = {
            view: selection.id,
            view_label: selection.label,
            window: { start: win.startDate, end: win.endDate, label: win.label },
            exported_at: new Date().toISOString(),
            money: 'USD',
            periods: Object.fromEntries(allColumns.map((c) => [c.key, c.scope])),
            rows: shown,
          };
          save(`numa-clients-${stamp}.json`, JSON.stringify(payload, null, 2), 'application/json');
        },
      },
    ];
  }, [shown, allColumns, selection.id, selection.label, win]);

  const infraView = FLEET_VIEWS.find((v) => v.kind === 'arcanum-infra');

  return (
    <Panel
      title="Stacks"
      subtitle={
        <>
          {plural(stackCount, 'stack')} in {selection.label}
          {oldCount > 0 && `. ${fmtN(oldCount)} with old data`}
        </>
      }
      flush
      actions={
        <>
          <SearchBox
            value={query}
            onChange={setQuery}
            placeholder="Search stacks"
            label="Search stacks by name or AWS account"
          />
          <RowDownloadMenu title="Download the table" options={downloads} onError={setDownloadError} />
        </>
      }
    >
      <div className="clients-toolbar">
        <div className="clients-toolbar__sets">
          <span className="clients-toolbar__label" id="clients-columns-label">
            Columns
          </span>
          <Nav
            variant="pills"
            className="nav-pills-custom clients-toolbar__pills"
            aria-labelledby="clients-columns-label"
            activeKey={setKey}
            onSelect={(key) => key && pickSet(key as SetKey)}
          >
            {SET_ORDER.map((k) => (
              <Nav.Item key={k}>
                <Nav.Link eventKey={k}>{sets[k].label}</Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </div>
        {oldCount > 0 && (
          <Form.Check
            type="switch"
            id="clients-old-only"
            className="clients-toolbar__switch"
            label="Old data only"
            checked={oldOnly}
            onChange={(e) => setOldOnly(e.target.checked)}
          />
        )}
      </div>

      {downloadError && <div className="clients-error">The download did not start: {downloadError}</div>}

      <div className="dash-scroll dash-scroll--tall">
        <Table hover className="dash-table clients-table">
          <thead>
            <tr>
              <SortTh column="name" sort={sort} onSort={onSort} className="clients-table__name">
                Stack
              </SortTh>
              {columns.map((c) => (
                <SortTh key={c.key} column={c.key} sort={sort} onSort={onSort} numeric hint={c.hint} scope={c.scope}>
                  {c.label}
                </SortTh>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1}>
                  <Empty>
                    {rows.length === 0
                      ? 'This view holds no stacks.'
                      : 'No stack matches. Clear the search or the old data switch.'}
                  </Empty>
                </td>
              </tr>
            ) : (
              shown.map((r) => {
                // A shared AWS account is one row. It opens no one stack: each stack on it has its own link.
                const shared = r.members.length > 1;
                return (
                  <tr
                    key={r.client}
                    className={shared ? undefined : 'dash-row'}
                    onClick={shared ? undefined : () => onSelectClient(r.client)}
                  >
                    <td className="clients-table__name">
                      <div className="clients-name">
                        {shared ? (
                          <span className="dash-name">{r.label}</span>
                        ) : (
                          // The row is the click target. The button gives it to the keyboard.
                          <LinkButton name title={`Open ${r.label}`}>
                            {r.label}
                          </LinkButton>
                        )}
                        {(r.kind === 'dev' || r.kind === 'internal') && <span className="clients-tag">{r.kind}</span>}
                        <FreshnessMark generatedAt={r.generatedAt} stale={r.stale} failed={r.failed} error={r.error} />
                      </div>
                      <div className="dash-sub clients-name__meta">{rowMeta(r, onSelectClient)}</div>
                    </td>
                    {columns.map((c) => (
                      <td key={c.key} className="numeric text-end">
                        {cellContent(c, r, pending)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {(sharedCount > 0 || approxShown || (infraCount > 0 && infraView) || hiddenCount > 0) && (
        <div className="dash-note dash-note--strip">
          {sharedCount > 0 && (
            <p>
              AWS bills an account, not a stack. Stacks that share an AWS account are one row here, with the
              account&apos;s bill counted once and each stack&apos;s own figures added up.
              {hiddenCount === 0 && ' The rows add up to the view.'}
            </p>
          )}
          {hiddenCount > 0 && (
            <p>
              {plural(hiddenCount, 'Inactive stack is', 'Inactive stacks are')} not listed. Their spend is in the
              view&apos;s spend figures, so the spend rows add up to less than the view. Turn on Show inactive in the
              list of stacks to see them.
            </p>
          )}
          {approxShown && <p>~ {APPROX_NOTE}</p>}
          {infraCount > 0 && infraView && (
            <p>
              {plural(infraCount, 'account in this view holds', 'accounts in this view hold')} no Numa stack and{' '}
              {infraCount === 1 ? 'is' : 'are'} not listed here.{' '}
              <LinkButton onClick={() => onSelectClient(infraView.id)}>Open {infraView.label}</LinkButton>
            </p>
          )}
        </div>
      )}
    </Panel>
  );
}

// ─── compare ─────────────────────────────────────────────────────────────

const MAX_COMPARE = 4;

// The theme's chart palette, by name. These four were checked together: each
// pair stays apart for a person with any of the common kinds of colour
// blindness. A line's dash pattern says which stack it is as well.
const [PURPLE, , , , GREEN, , , SKY, LIME, , , , , ROSE] = ND_CHART_PAL;
const SERIES_COLOURS = [PURPLE, SKY, GREEN, ROSE];
const SERIES_DASHES: number[][] = [[], [], [7, 4], [2, 3]];

const CHART_METRICS: Array<{ key: SeriesKey; label: string; money?: boolean }> = [
  { key: 'spend', label: 'Total spend', money: true },
  { key: 'chatCost', label: 'Model spend from chat and agents', money: true },
  { key: 'conversations', label: 'Conversations' },
  { key: 'messages', label: 'Messages' },
  { key: 'peopleActive', label: 'Active people' },
  { key: 'scheduledRuns', label: 'Scheduled runs' },
  { key: 'credits', label: 'Credits used' },
  { key: 'appRuns', label: 'App runs' },
];

interface CompareProps {
  rows: StackRow[];
  timeSaved: Scope;
  pending: FleetTier[];
  stacks: ClientSnapshot[];
  fleet: FleetData;
  selection: Selection;
  win: WindowState;
  onSelectClient: (id: string | null) => void;
}

function Compare({ rows, timeSaved, pending, stacks, fleet, selection, win, onSelectClient }: CompareProps) {
  useCurrency();
  // A stack keeps its place, and so its colour, when another is added or taken away.
  const [slots, setSlots] = useState<Array<string | null> | null>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [chartKey, setChartKey] = useState<SeriesKey>('spend');

  const byClient = useMemo(() => new Map(rows.map((r) => [r.client, r])), [rows]);
  const snapshotOf = useMemo(() => new Map(stacks.map((s) => [s.client, s])), [stacks]);

  // Until a person picks: the two stacks that cost the most in the window.
  const effective = useMemo(() => {
    const base = slots ?? [rows[0]?.client ?? null, rows[1]?.client ?? null];
    return Array.from({ length: MAX_COMPARE }, (_, i) => {
      const client = base[i];
      return client && byClient.has(client) ? client : null;
    });
  }, [slots, rows, byClient]);

  const picked = useMemo(
    () =>
      effective.flatMap((client, slot) => {
        const row = client ? byClient.get(client) : undefined;
        return row ? [{ row, slot }] : [];
      }),
    [effective, byClient]
  );
  const full = picked.length >= MAX_COMPARE;

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const chosen = new Set(effective);
    return rows.filter((r) => !chosen.has(r.client) && (!q || r.label.toLowerCase().includes(q))).slice(0, 8);
  }, [rows, query, effective]);

  const add = (client: string) => {
    const next = [...effective];
    const free = next.indexOf(null);
    if (free === -1) return;
    next[free] = client;
    setSlots(next);
    setQuery('');
    setOpen(false);
  };
  const remove = (slot: number) => setSlots(effective.map((c, i) => (i === slot ? null : c)));

  const sets = useMemo(() => columnSets(timeSaved), [timeSaved]);

  const metric = CHART_METRICS.find((m) => m.key === chartKey) ?? CHART_METRICS[0];
  const series = useMemo(
    () =>
      picked.map(({ row, slot }) => {
        const snap = snapshotOf.get(row.client);
        // A standalone customer's bill is cut to Numa's spend before it is charted.
        return { row, slot, answer: snap ? selectSeries(snapshotForDisplay(snap), win, chartKey) : null };
      }),
    [picked, snapshotOf, win, chartKey]
  );
  const waiting = needsOf(...series.map((s) => s.answer));
  const waitingKey = waiting.join(',');
  const { ensureTier } = fleet;
  useEffect(() => {
    waiting.forEach((tier) => ensureTier(tier));
  }, [waitingKey, ensureTier]);

  const drawn = series.flatMap(({ row, slot, answer }) =>
    answer && isReady(answer) && answer.available ? [{ row, slot, series: answer }] : []
  );
  const noRecord = series
    .filter((s) => !s.answer || (isReady(s.answer) && !s.answer.available))
    .map((s) => s.row.label);
  const chartNotes = Array.from(new Set(drawn.map((d) => d.series.note).filter((n): n is string => Boolean(n))));

  const fmtPoint = (v: number) => (metric.money ? fmtUSD(v) : metric.key === 'credits' ? fmtCredits(v) : fmtN(v));
  const chartOptions: ChartOptions<'line'> = {
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: { color: ND_COLORS.textDim, usePointStyle: true, pointStyle: 'line', boxWidth: 28 },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${fmtPoint(Number(ctx.parsed.y))}`,
        },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 10 } },
      y: metric.money ? moneyScale() : { beginAtZero: true, ticks: { callback: (v) => fmtN(Number(v)) } },
    },
  };

  return (
    <>
      {/* The list of stacks drops below this panel, so the panel must not cut it off. */}
      <div className="clients-pick">
        <Panel
          title="Compare stacks"
          subtitle={`Two to ${MAX_COMPARE} stacks from ${selection.label}, side by side.`}
          actions={
            <div className="clients-picker">
              <Form.Control
                type="search"
                size="sm"
                className="dash-search"
                placeholder={full ? 'Remove a stack to add another' : 'Add a stack'}
                aria-label="Add a stack to compare"
                role="combobox"
                aria-expanded={open && !full}
                aria-controls="clients-picker-list"
                disabled={full}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onBlur={() => setOpen(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && matches[0]) add(matches[0].client);
                  if (e.key === 'Escape') setOpen(false);
                }}
              />
              {open && !full && (
                <ul className="clients-picker__list" id="clients-picker-list" role="listbox">
                  {matches.length === 0 ? (
                    <li className="clients-picker__none">No stack matches</li>
                  ) : (
                    matches.map((r) => (
                      <li key={r.client} role="option" aria-selected="false">
                        {/* Mouse down, not click: the list closes when the box loses the cursor. */}
                        <button
                          type="button"
                          className="clients-picker__option"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            add(r.client);
                          }}
                        >
                          <span className="clients-picker__name">{r.label}</span>
                          <span className="clients-picker__spend numeric">
                            {r.spend === null ? '' : fmtUSD0(r.spend)}
                          </span>
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
          }
        >
          {picked.length === 0 ? (
            <Empty>Add two or more stacks to compare them.</Empty>
          ) : (
            <div className="clients-chips">
              {picked.map(({ row, slot }) => (
                <span key={row.client} className="clients-chip">
                  <Swatch colour={SERIES_COLOURS[slot]} />
                  {row.label}
                  <button
                    type="button"
                    className="clients-chip__remove"
                    onClick={() => remove(slot)}
                    aria-label={`Remove ${row.label}`}
                    title={`Remove ${row.label}`}
                  >
                    <XLg size={10} />
                  </button>
                </span>
              ))}
              {picked.length === 1 && <span className="clients-chips__hint">Add one more to compare.</span>}
            </div>
          )}
        </Panel>
      </div>

      {picked.length > 0 && (
        <Panel title="Side by side" subtitle="A bar shows each number against the largest in its row." flush>
          <div className="dash-scroll dash-scroll--free">
            <Table className="clients-compare">
              <thead>
                <tr>
                  <th>Metric</th>
                  {picked.map(({ row, slot }) => (
                    <th key={row.client} className="text-end">
                      <div className="clients-compare__stack">
                        <Swatch colour={SERIES_COLOURS[slot]} />
                        <LinkButton name title={`Open ${row.label}`} onClick={() => onSelectClient(row.client)}>
                          {row.label}
                        </LinkButton>
                      </div>
                      <FreshnessMark
                        generatedAt={row.generatedAt}
                        stale={row.stale}
                        failed={row.failed}
                        error={row.error}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              {SET_ORDER.map((k) => (
                <tbody key={k}>
                  <tr className="clients-compare__group">
                    <th colSpan={picked.length + 1} scope="colgroup">
                      {sets[k].label}
                    </th>
                  </tr>
                  {sets[k].columns.map((c) => {
                    const values = picked.map(({ row }) => c.value(row));
                    const max = Math.max(0, ...values.map((v) => v ?? 0));
                    return (
                      <tr key={c.key}>
                        <th scope="row" className="clients-compare__metric" title={c.hint}>
                          {c.long ?? c.label}
                          <ScopeTag scope={c.scope} />
                        </th>
                        {picked.map(({ row }, i) => {
                          const v = values[i];
                          return (
                            <td key={row.client} className="numeric text-end">
                              {cellContent(c, row, pending)}
                              {c.bar && (
                                <Bar
                                  fraction={v !== null && max > 0 ? v / max : 0}
                                  className="dash-bar--under clients-bar"
                                />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              ))}
            </Table>
          </div>
          {picked.some(({ row }) => row.conversationsApprox) && (
            <div className="dash-note dash-note--strip">
              <p>~ {APPROX_NOTE}</p>
            </div>
          )}
        </Panel>
      )}

      {picked.length > 0 && (
        <Panel
          title="Day by day"
          subtitle={metric.key === 'peopleActive' ? 'People active on each day. Do not add the days up.' : undefined}
          actions={
            <Form.Select
              size="sm"
              className="clients-metric"
              aria-label="What the chart shows"
              value={chartKey}
              onChange={(e) => setChartKey(e.target.value as SeriesKey)}
            >
              {CHART_METRICS.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </Form.Select>
          }
        >
          {waiting.length > 0 ? (
            <Empty>Loading {metric.label.toLowerCase()} by day</Empty>
          ) : drawn.length === 0 ? (
            <Empty>These stacks hold no record of {metric.label.toLowerCase()} by day.</Empty>
          ) : (
            <div className="dash-chart dash-chart--tall">
              <Line
                data={{
                  labels: drawn[0].series.days.map(dayLabel),
                  datasets: drawn.map(({ row, slot, series: s }) => ({
                    label: row.label,
                    data: s.values,
                    borderColor: SERIES_COLOURS[slot],
                    backgroundColor: SERIES_COLOURS[slot],
                    borderDash: SERIES_DASHES[slot],
                    borderWidth: 2,
                    pointRadius: 0,
                    pointHoverRadius: 4,
                    tension: 0.25,
                  })),
                }}
                options={chartOptions}
              />
            </div>
          )}
          {waiting.length === 0 && drawn.length > 0 && (noRecord.length > 0 || chartNotes.length > 0) && (
            <div className="dash-note">
              {noRecord.length > 0 && <p>No record of this by day for: {noRecord.join(', ')}.</p>}
              {chartNotes.map((n) => (
                <p key={n}>{n}</p>
              ))}
            </div>
          )}
        </Panel>
      )}
    </>
  );
}

// ─── the health map ──────────────────────────────────────────────────────

// The map's colours, from the theme. Status colours carry their meaning here:
// green is good, amber is a caution, red is a problem, grey is nothing to say.
const MAP_COLOURS = {
  good: ND_COLORS.good,
  fair: LIME,
  caution: ND_COLORS.warn,
  bad: ND_COLORS.bad,
  none: ND_COLORS.neutral,
};

const TREND_WORD: Record<FleetMapMetrics['trendDirection'], string> = {
  growing: 'growing',
  flat: 'steady',
  declining: 'falling',
  unknown: 'nothing to compare',
};

function healthColour(m: FleetMapMetrics): string {
  if (m.stale) return MAP_COLOURS.none;
  if (m.trendDirection === 'growing') return MAP_COLOURS.good;
  if (m.trendDirection === 'flat') return MAP_COLOURS.caution;
  if (m.trendDirection === 'declining') return MAP_COLOURS.bad;
  return MAP_COLOURS.none;
}

function costColour(m: FleetMapMetrics): string {
  if (m.stale) return MAP_COLOURS.none;
  if (m.costPerActiveIntensity === 'light') return MAP_COLOURS.good;
  if (m.costPerActiveIntensity === 'moderate') return MAP_COLOURS.fair;
  if (m.costPerActiveIntensity === 'heavy') return MAP_COLOURS.caution;
  if (m.costPerActiveIntensity === 'very_heavy') return MAP_COLOURS.bad;
  return MAP_COLOURS.none;
}

/** What a person reads when the pointer is on a stack. The first line is the name. */
function hoverLines(m: FleetMapMetrics, label: string, mode: FleetMode): string[] {
  const lines = [label];
  if (m.stale) lines.push(`Old data: runs to ${dayLabel(m.asOf)}`);
  if (mode === 'health') {
    const share = m.provisionedUsers > 0 ? ` (${(m.activityRatio * 100).toFixed(0)}%)` : '';
    lines.push(`${fmtN(m.activeUsers30d)} of ${fmtN(m.provisionedUsers)} people active${share}`);
    if (m.trendDirection === 'unknown') lines.push('No use in the last 60 days');
    else if (m.userDaysPrior30d === 0) lines.push('Use over 30 days: new');
    else lines.push(`Use over 30 days: ${TREND_WORD[m.trendDirection]} (${signedPct(m.trend30d)})`);
    if (m.pulseDirection && m.userDaysPrior7d > 0) lines.push(`Last 7 days: ${signedPct(m.pulseDelta)}`);
  } else {
    lines.push(`${fmtUSD0(m.cost30d)} spend over 30 days`);
    lines.push(`${fmtUSD0(m.costPerSeat)} for each provisioned person`);
    lines.push(
      Number.isFinite(m.costPerActiveUser) ? `${fmtUSD0(m.costPerActiveUser)} for each active person` : 'Nobody active'
    );
    if (m.costPulseDirection && m.costPrior7d > 0) lines.push(`Spend over 7 days: ${signedPct(m.costPulseDelta)}`);
  }
  return lines;
}

interface MapProps {
  rows: StackRow[];
  pending: FleetTier[];
  stacks: ClientSnapshot[];
  fleet: FleetData;
  selection: Selection;
  onSelectClient: (id: string | null) => void;
}

function HealthMap({ rows, pending, stacks, fleet, selection, onSelectClient }: MapProps) {
  const [mode, setMode] = useState<FleetMode>('health');
  // The cost map needs the bill by service on a stack whose data has no ready-made model lines.
  const tiers: FleetTier[] = mode === 'cost' && pending.includes('services') ? ['users', 'services'] : ['users'];
  return (
    <NeedsTier fleet={fleet} tiers={tiers} what="Per-person history">
      <MapBody
        rows={rows}
        stacks={stacks}
        selection={selection}
        mode={mode}
        onMode={setMode}
        onSelectClient={onSelectClient}
      />
    </NeedsTier>
  );
}

interface MapBodyProps extends Omit<MapProps, 'pending' | 'fleet'> {
  mode: FleetMode;
  onMode: (mode: FleetMode) => void;
}

function MapBody({ rows, stacks, selection, mode, onMode, onSelectClient }: MapBodyProps) {
  useCurrency();
  const [query, setQuery] = useState('');

  const map = useMemo(() => computeFleetMap(stacks), [stacks]);
  const labelOf = useMemo(() => new Map(rows.map((r) => [r.client, r.label])), [rows]);

  // A stack with nobody provisioned has no cost per person: it cannot be placed on the cost map.
  const placeable = useMemo(
    () => (mode === 'cost' ? map.metrics.filter((m) => m.provisionedUsers > 0) : map.metrics),
    [map.metrics, mode]
  );
  const unplaced = map.metrics.length - placeable.length;

  // A search fades the stacks that do not match. Nothing moves, so a person keeps their bearings.
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return new Set(
      placeable.filter((m) => (labelOf.get(m.client) ?? m.client).toLowerCase().includes(q)).map((m) => m.client)
    );
  }, [placeable, query, labelOf]);

  const planets = useMemo(() => {
    const metricOf = new Map(placeable.map((m) => [m.client, m]));
    return buildFleetPlanets(placeable, map.stats, mode).map((p) => {
      const m = metricOf.get(p.key);
      if (!m) return p;
      const label = labelOf.get(m.client) ?? m.client;
      return {
        ...p,
        // The name is drawn where it fits. Every name is on the hover card.
        label,
        fillColor: mode === 'health' ? healthColour(m) : costColour(m),
        tooltipLines: hoverLines(m, label, mode),
      };
    });
  }, [placeable, map.stats, mode, labelOf]);

  const ringNames = fleetRingLabels(mode);
  const ringCounts = useMemo(() => {
    const counts = [0, 0, 0, 0];
    for (const p of planets) if (p.ringIndex >= 0 && p.ringIndex < 4) counts[p.ringIndex] += 1;
    return counts;
  }, [planets]);
  const staleCount = placeable.filter((m) => m.stale).length;
  const seat = map.stats.costPerSeatBands;
  const active = map.stats.costPerActiveBands;

  const ringMeaning =
    mode === 'health'
      ? [
          'Half or more of the provisioned people were active',
          'A fifth to a half were active',
          'Under a fifth were active',
          'Nobody was active',
        ]
      : [
          `Up to ${fmtUSD0(seat.efficientMax)} for each provisioned person`,
          `Up to ${fmtUSD0(seat.typicalMax)}`,
          `Up to ${fmtUSD0(seat.expensiveMax)}`,
          `Above ${fmtUSD0(seat.expensiveMax)}`,
        ];
  const colourKey: Array<{ colour: string; words: string }> =
    mode === 'health'
      ? [
          { colour: MAP_COLOURS.good, words: 'Use is growing: up more than 20% on the 30 days before' },
          { colour: MAP_COLOURS.caution, words: 'Use is steady' },
          { colour: MAP_COLOURS.bad, words: 'Use is falling: down more than 20%' },
          { colour: MAP_COLOURS.none, words: 'No use to compare, or old data' },
        ]
      : [
          { colour: MAP_COLOURS.good, words: `Up to ${fmtUSD0(active.lightMax)} for each active person` },
          { colour: MAP_COLOURS.fair, words: `Up to ${fmtUSD0(active.moderateMax)}` },
          { colour: MAP_COLOURS.caution, words: `Up to ${fmtUSD0(active.heavyMax)}` },
          { colour: MAP_COLOURS.bad, words: `Above ${fmtUSD0(active.heavyMax)}` },
          { colour: MAP_COLOURS.none, words: 'Nobody active, or old data' },
        ];

  return (
    <div className="dash-cols dash-cols--2-1">
      <Panel
        title={mode === 'health' ? 'Health map' : 'Cost map'}
        scope="last-30d"
        subtitle={
          <>
            Each stack over the 30 days up to its own last nightly update: {dayLabel(map.asOf)} for a stack that is up
            to date. Select a stack to open it.
          </>
        }
        actions={
          <>
            <SearchBox value={query} onChange={setQuery} placeholder="Search stacks" label="Search stacks on the map" />
            <Nav
              variant="pills"
              className="nav-pills-custom"
              aria-label="What the map shows"
              activeKey={mode}
              onSelect={(key) => key && onMode(key as FleetMode)}
            >
              <Nav.Item>
                <Nav.Link eventKey="health">Health</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="cost">Cost</Nav.Link>
              </Nav.Item>
            </Nav>
          </>
        }
      >
        {planets.length === 0 ? (
          <Empty>{selection.label} holds no stack to draw.</Empty>
        ) : (
          <div className="clients-map">
            <Constellation
              planets={planets}
              ringLabels={ringNames}
              centerLabel="NUMA"
              selectedKey={null}
              highlightKeys={matches}
              onSelect={(key) => {
                if (key) onSelectClient(key);
              }}
            />
          </div>
        )}
        <div className="dash-note">
          <p>
            {plural(planets.length, 'stack')} drawn.
            {matches &&
              (matches.size > 0
                ? ` ${plural(matches.size, 'matches', 'match')} the search.`
                : ' No stack matches the search.')}
            {staleCount > 0 &&
              ` ${plural(staleCount, 'has old data and is', 'have old data and are')} grey, drawn as at the last update.`}
            {unplaced > 0 &&
              ` ${plural(unplaced, 'has nobody provisioned and is', 'have nobody provisioned and are')} not placed.`}
          </p>
        </div>
      </Panel>

      <Panel title="How to read it">
        <dl className="clients-legend">
          <dt>Ring</dt>
          <dd>
            {mode === 'health'
              ? 'The share of provisioned people who were active. Closer to the centre is better. A stack with few people is pulled toward the average of the stacks drawn, so one person of two does not count as half.'
              : 'Spend for each provisioned person over 30 days. Closer to the centre costs less.'}
            <ul className="clients-legend__rings">
              {ringNames.map((name, i) => (
                <li key={name}>
                  <span className="clients-legend__ring">{name}</span>
                  <span className="clients-legend__meaning">{ringMeaning[i]}</span>
                  <span className="clients-legend__count numeric">{fmtN(ringCounts[i])}</span>
                </li>
              ))}
            </ul>
          </dd>
          <dt>Size</dt>
          <dd>Provisioned people. The same on both maps.</dd>
          <dt>Colour</dt>
          <dd>
            <ul className="clients-legend__colours">
              {colourKey.map((c) => (
                <li key={c.words}>
                  <Swatch colour={c.colour} />
                  {c.words}
                </li>
              ))}
            </ul>
          </dd>
          <dt>Arrow</dt>
          <dd>
            {mode === 'health' ? 'Use' : 'Spend'} over the last 7 days is more than 20% up or down on the 7 days before.
          </dd>
        </dl>
      </Panel>
    </div>
  );
}

// ─── the tab ─────────────────────────────────────────────────────────────

type Mode = 'table' | 'compare' | 'map';

export function Clients({ window: win, fleet, selection, onSelectClient }: DashboardTabProps) {
  const [mode, setMode] = useState<Mode>('table');

  // Inactive stacks the view hides are not in `selection.stacks`: no row, no dot, no compare.
  const rules = useMemo(
    () => ({ contractOf: selection.contractOf, inactive: selection.inactive }),
    [selection.contractOf, selection.inactive]
  );
  // The table: one row for each AWS account, so a bill that several stacks share is counted once.
  const tableRows = useMemo(
    () => selectStackRows(selection.stacks, win, fleet.freshness, { onePerAccount: true, ...rules }),
    [selection.stacks, win, fleet.freshness, rules]
  );
  // Compare and the map set single stacks side by side: one row for each stack.
  const stackRows = useMemo(
    () => selectStackRows(selection.stacks, win, fleet.freshness, rules),
    [selection.stacks, win, fleet.freshness, rules]
  );
  // Stacks only. An account with no Numa stack has no people and no chat to compare.
  const folded = useMemo(() => tableRows.rows.filter((r) => r.kind !== 'infra'), [tableRows]);
  const rows = useMemo(() => stackRows.rows.filter((r) => r.kind !== 'infra'), [stackRows]);
  const stacks = useMemo(() => selection.stacks.filter((s) => !isInfraAccount(s)), [selection.stacks]);
  const infraCount = stackRows.rows.length - rows.length;

  // Fill the cells that wait for heavier data. The table draws at once and they arrive after.
  const pendingKey = stackRows.pending.join(',');
  const { ensureTier } = fleet;
  useEffect(() => {
    stackRows.pending.forEach((tier) => ensureTier(tier));
  }, [pendingKey, ensureTier]);

  return (
    <div className="clients">
      <Nav
        variant="pills"
        className="nav-pills-custom clients-modes"
        activeKey={mode}
        onSelect={(key) => key && setMode(key as Mode)}
      >
        <Nav.Item>
          <Nav.Link eventKey="table">Table</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="compare">Compare</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="map">Health map</Nav.Link>
        </Nav.Item>
      </Nav>

      {mode === 'table' && (
        <StacksTable
          rows={folded}
          timeSaved={stackRows.scopes.timeSaved}
          pending={stackRows.pending}
          infraCount={infraCount}
          hiddenCount={selection.costOnly.filter((s) => !isInfraAccount(s)).length}
          selection={selection}
          win={win}
          onSelectClient={onSelectClient}
        />
      )}
      {mode === 'compare' && (
        <Compare
          rows={rows}
          timeSaved={stackRows.scopes.timeSaved}
          pending={stackRows.pending}
          stacks={stacks}
          fleet={fleet}
          selection={selection}
          win={win}
          onSelectClient={onSelectClient}
        />
      )}
      {mode === 'map' && (
        <HealthMap
          rows={rows}
          pending={stackRows.pending}
          stacks={stacks}
          fleet={fleet}
          selection={selection}
          onSelectClient={onSelectClient}
        />
      )}
    </div>
  );
}
